import React, { useState, useEffect } from 'react';
import { usuarioAPI, moduloAPI } from '../lib/api';
import { formatDate, getRolLabel } from '../lib/utils';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Checkbox } from '../components/ui/checkbox';
import { ScrollArea, ScrollBar } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { Users, Search, RefreshCw, Edit, Trash2, Loader2, User, Shield, Briefcase, Blocks } from 'lucide-react';

export default function UsuariosPage() {
    const [usuarios, setUsuarios] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedUser, setSelectedUser] = useState(null);
    const [editData, setEditData] = useState({ nombre: '', rol: '', modulos_acceso: [] });
    const [processing, setProcessing] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [empresaModulos, setEmpresaModulos] = useState([]);

    useEffect(() => {
        loadUsuarios();
        loadEmpresaModulos();
    }, []);

    useEffect(() => {
        filterUsers();
    }, [usuarios, searchTerm]);

    const loadEmpresaModulos = async () => {
        try {
            const res = await moduloAPI.getEmpresa();
            setEmpresaModulos(res.data);
        } catch (err) {
            console.error('Error loading empresa modules:', err);
        }
    };

    const loadUsuarios = async () => {
        setLoading(true);
        try {
            const response = await usuarioAPI.getAll();
            setUsuarios(response.data);
        } catch (err) {
            console.error('Error loading usuarios:', err);
            toast.error('Error al cargar usuarios');
        } finally {
            setLoading(false);
        }
    };

    const filterUsers = () => {
        if (!searchTerm) {
            setFilteredUsers(usuarios);
            return;
        }

        const term = searchTerm.toLowerCase();
        setFilteredUsers(
            usuarios.filter(
                (user) =>
                    user.nombre.toLowerCase().includes(term) ||
                    user.email.toLowerCase().includes(term) ||
                    user.rol.toLowerCase().includes(term)
            )
        );
    };

    const openEditDialog = (user) => {
        setSelectedUser(user);
        setEditData({
            nombre: user.nombre,
            rol: user.rol,
            modulos_acceso: user.modulos_acceso || [],
        });
    };

    const closeEditDialog = () => {
        setSelectedUser(null);
        setEditData({ nombre: '', rol: '', modulos_acceso: [] });
    };

    const handleUpdate = async () => {
        if (!selectedUser) return;

        setProcessing(true);
        try {
            await usuarioAPI.update(selectedUser.id, editData);
            toast.success('Usuario actualizado');
            closeEditDialog();
            loadUsuarios();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al actualizar');
        } finally {
            setProcessing(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteConfirm) return;

        setProcessing(true);
        try {
            await usuarioAPI.delete(deleteConfirm.id);
            toast.success('Usuario eliminado');
            setDeleteConfirm(null);
            loadUsuarios();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al eliminar');
        } finally {
            setProcessing(false);
        }
    };

    const getRolIcon = (rol) => {
        switch (rol) {
            case 'admin':
                return <Shield className="h-4 w-4" />;
            case 'gerente':
                return <Briefcase className="h-4 w-4" />;
            case 'seguridad':
                return <Shield className="h-4 w-4" />;
            default:
                return <User className="h-4 w-4" />;
        }
    };

    const getRolBadgeColor = (rol) => {
        switch (rol) {
            case 'admin':
                return 'bg-purple-100 text-purple-700 border-purple-300';
            case 'gerente':
                return 'bg-blue-100 text-blue-700 border-blue-300';
            case 'seguridad':
                return 'bg-amber-100 text-amber-700 border-amber-300';
            default:
                return 'bg-slate-100 text-slate-700 border-slate-300';
        }
    };

    return (
        <Layout>
            <div className="space-y-6 animate-fade-in">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                        <h1 className="font-heading text-3xl md:text-4xl font-black uppercase tracking-tight">
                            Usuarios
                        </h1>
                        <p className="text-muted-foreground mt-1">
                            {filteredUsers.length} usuario{filteredUsers.length !== 1 ? 's' : ''} registrado{filteredUsers.length !== 1 ? 's' : ''}
                        </p>
                    </div>
                    <Button
                        variant="outline"
                        onClick={loadUsuarios}
                        className="border-2"
                        data-testid="refresh-btn"
                    >
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Actualizar
                    </Button>
                </div>

                {/* Search */}
                <Card className="border-2">
                    <CardContent className="p-4">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                placeholder="Buscar por nombre, email o rol..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10 input-industrial"
                                data-testid="search-input"
                            />
                        </div>
                    </CardContent>
                </Card>

                {/* Table */}
                <Card className="border-2">
                    <ScrollArea className="w-full">
                        <Table className="table-industrial">
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Usuario</TableHead>
                                    <TableHead>Email</TableHead>
                                    <TableHead>Rol</TableHead>
                                    <TableHead>Modulos</TableHead>
                                    <TableHead>Fecha Registro</TableHead>
                                    <TableHead className="text-right">Acciones</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {loading ? (
                                    <>
                                        {[1, 2, 3, 4].map((i) => (
                                            <TableRow key={i}>
                                                <TableCell><Skeleton className="h-5 w-32" /></TableCell>
                                                <TableCell><Skeleton className="h-5 w-40" /></TableCell>
                                                <TableCell><Skeleton className="h-5 w-24" /></TableCell>
                                                <TableCell><Skeleton className="h-5 w-32" /></TableCell>
                                                <TableCell><Skeleton className="h-5 w-24" /></TableCell>
                                                <TableCell><Skeleton className="h-8 w-20 ml-auto" /></TableCell>
                                            </TableRow>
                                        ))}
                                    </>
                                ) : filteredUsers.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                                            {searchTerm ? 'No se encontraron usuarios' : 'No hay usuarios registrados'}
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    filteredUsers.map((user) => (
                                        <TableRow key={user.id} data-testid={`row-${user.id}`}>
                                            <TableCell>
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-sm bg-primary/10 flex items-center justify-center">
                                                        <User className="h-5 w-5 text-primary" />
                                                    </div>
                                                    <span className="font-medium">{user.nombre}</span>
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-muted-foreground">
                                                {user.email}
                                            </TableCell>
                                            <TableCell>
                                                <Badge 
                                                    variant="outline" 
                                                    className={`${getRolBadgeColor(user.rol)} uppercase text-xs`}
                                                >
                                                    {getRolIcon(user.rol)}
                                                    <span className="ml-1">{getRolLabel(user.rol)}</span>
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                {(!user.modulos_acceso || user.modulos_acceso.length === 0) ? (
                                                    <Badge variant="outline" className="text-xs text-muted-foreground">Todos</Badge>
                                                ) : (
                                                    <div className="flex flex-wrap gap-1">
                                                        {user.modulos_acceso.map(m => {
                                                            const mod = empresaModulos.find(em => em.codigo === m);
                                                            return (
                                                                <Badge key={m} variant="outline" className="text-xs">
                                                                    {mod?.nombre || m}
                                                                </Badge>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-muted-foreground text-sm">
                                                {formatDate(user.created_at)}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <div className="flex justify-end gap-2">
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => openEditDialog(user)}
                                                        data-testid={`edit-${user.id}`}
                                                    >
                                                        <Edit className="h-4 w-4" />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => setDeleteConfirm(user)}
                                                        className="text-destructive"
                                                        data-testid={`delete-${user.id}`}
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                        <ScrollBar orientation="horizontal" />
                    </ScrollArea>
                </Card>

                {/* Edit Dialog */}
                <Dialog open={!!selectedUser} onOpenChange={() => closeEditDialog()}>
                    <DialogContent className="sm:max-w-md">
                        <DialogHeader>
                            <DialogTitle className="font-heading uppercase">Editar Usuario</DialogTitle>
                            <DialogDescription>{selectedUser?.email}</DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                            <div className="space-y-2">
                                <Label className="input-label">Nombre</Label>
                                <Input
                                    value={editData.nombre}
                                    onChange={(e) => setEditData({ ...editData, nombre: e.target.value })}
                                    className="input-industrial"
                                    data-testid="edit-nombre"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="input-label">Rol</Label>
                                <Select 
                                    value={editData.rol} 
                                    onValueChange={(v) => setEditData({ ...editData, rol: v })}
                                >
                                    <SelectTrigger className="input-industrial" data-testid="edit-rol">
                                        <SelectValue placeholder="Seleccionar rol" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="admin">Administrador</SelectItem>
                                        <SelectItem value="solicitante">Solicitante</SelectItem>
                                        <SelectItem value="gerente">Gerente</SelectItem>
                                        <SelectItem value="seguridad">Seguridad</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            {empresaModulos.length > 0 && (
                                <div className="space-y-2">
                                    <Label className="input-label flex items-center gap-2">
                                        <Blocks className="h-4 w-4" /> Modulos de Acceso
                                    </Label>
                                    <p className="text-xs text-muted-foreground">
                                        Si no se selecciona ninguno, el usuario ve todos los modulos de la empresa.
                                    </p>
                                    <div className="space-y-2 border rounded-lg p-3">
                                        {empresaModulos.map(mod => {
                                            const checked = editData.modulos_acceso.includes(mod.codigo);
                                            return (
                                                <div key={mod.codigo} className="flex items-center gap-3">
                                                    <Checkbox
                                                        id={`mod-${mod.codigo}`}
                                                        checked={checked}
                                                        onCheckedChange={(val) => {
                                                            setEditData(prev => ({
                                                                ...prev,
                                                                modulos_acceso: val
                                                                    ? [...prev.modulos_acceso, mod.codigo]
                                                                    : prev.modulos_acceso.filter(c => c !== mod.codigo),
                                                            }));
                                                        }}
                                                        data-testid={`mod-check-${mod.codigo}`}
                                                    />
                                                    <label htmlFor={`mod-${mod.codigo}`} className="text-sm cursor-pointer">
                                                        {mod.nombre}
                                                    </label>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={closeEditDialog} className="border-2">
                                Cancelar
                            </Button>
                            <Button
                                onClick={handleUpdate}
                                disabled={processing}
                                className="btn-industrial bg-primary text-primary-foreground"
                                data-testid="save-edit-btn"
                            >
                                {processing ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Guardar'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Delete Confirmation */}
                <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
                    <DialogContent className="sm:max-w-md">
                        <DialogHeader>
                            <DialogTitle className="font-heading uppercase text-destructive">
                                Eliminar Usuario
                            </DialogTitle>
                            <DialogDescription>
                                ¿Está seguro que desea eliminar a {deleteConfirm?.nombre}?
                            </DialogDescription>
                        </DialogHeader>
                        <div className="py-4">
                            <p className="text-sm text-muted-foreground">
                                Esta acción no se puede deshacer. El usuario perderá acceso al sistema.
                            </p>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setDeleteConfirm(null)} className="border-2">
                                Cancelar
                            </Button>
                            <Button
                                onClick={handleDelete}
                                disabled={processing}
                                className="btn-industrial bg-destructive text-destructive-foreground"
                                data-testid="confirm-delete-btn"
                            >
                                {processing ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Eliminar'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </Layout>
    );
}
